import { uniqBy } from 'lodash/array';
import { orderBy } from 'lodash/collection';
import { isEmpty, isEqual } from 'lodash/lang';
import { assignInWith, has } from 'lodash/object';

import { FRS_LOADED, FRS_READY } from 'components/eui/EuiFiltersContext/reducer';
import {
  EMPTY_VALUE_ID,
  EMPTY_VALUE_NAME,
  GENERAL_INTEREST_ID,
  GENERAL_INTEREST_NAME
} from 'components/pages/pre-screen/PatientDatabase/constants';

const SELECTED_OPTION_GROUP = 1;
const UNSELECTED_OPTION_GROUP = 2;
const STUDY_SELECTION_TYPE = 'Study';
const SITE_SELECTION_TYPE = 'Site';
const STUDY_FILTER = 'study';
const SITE_FILTER = 'site';
const THERAPEUTIC_AREA_FILTER = 'therapeuticArea';
const INDICATION_FILTER = 'indication';
const RECALCULATION_LIMIT = 5;

export function initializeProcessor(state, action) {
  const { payload } = action;
  const { key, studySites } = payload;
  const previousFilterState = state[key] || {};

  const selectedStudyMap = resolveSelectedMap(payload, 'selectedStudyMap', previousFilterState.selectedStudyMap);
  const selectedSiteMap = resolveSelectedMap(payload, 'selectedSiteMap', previousFilterState.selectedSiteMap);

  const newState = recalculatePatientDatabaseFilters({
    ...state,
    [key]: {
      ...previousFilterState,
      studySites,
      selectedStudyMap,
      selectedSiteMap,
      studies: [],
      sites: [],
      selectedStudies: [],
      selectedSites: []
    },
    therapeuticArea: {
      source: [],
      value: [],
      ...state.therapeuticArea
    },
    indication: {
      source: [],
      value: [],
      ...state.indication
    }
  });
  return {
    ...newState,
    schema: setFilterStatuses(state.schema, FRS_LOADED)
  };
}

function resolveSelectedMap(payload, mapKey, previousSelectedMap) {
  if (has(payload, mapKey)) {
    return payload[mapKey] || {};
  }
  if (isEmpty(previousSelectedMap)) {
    return {};
  }
  return previousSelectedMap;
}

export function syncCacheProcessor(state, action) {
  const { payload: cache } = action;
  const hasRootSsuCache = has(cache, 'selectedStudyMap') || has(cache, 'selectedSiteMap');
  const ssuCache = hasRootSsuCache ? cache : cache?.ssu;

  const nextState = recalculatePatientDatabaseFilters({
    ...state,
    ssu: {
      ...state.ssu,
      ...(isEmpty(ssuCache) ? {} : ssuCache)
    },
    therapeuticArea: {
      ...state.therapeuticArea,
      ...(isEmpty(cache?.therapeuticArea) ? {} : cache.therapeuticArea)
    },
    indication: {
      ...state.indication,
      ...(isEmpty(cache?.indication) ? {} : cache.indication)
    }
  });

  return {
    ...nextState,
    schema: setFilterStatuses(state.schema, FRS_READY)
  };
}

export function setValueProcessor(state, action) {
  const { payload } = action;
  const { key, selectionType, value } = payload;
  const previousFilterState = state[key];

  if (key === 'ssu') {
    const { selectedStudyMap, selectedSiteMap } = previousFilterState;
    let newState;

    if (selectionType === STUDY_SELECTION_TYPE) {
      const studyIds = collectIdsMap(value);

      if (isEqual(studyIds, selectedStudyMap)) return state;

      newState = {
        ...state,
        [key]: {
          ...previousFilterState,
          selectedStudyMap: mergeMaps(studyIds, selectedStudyMap)
        }
      };
    }

    if (selectionType === SITE_SELECTION_TYPE) {
      const siteIds = collectIdsMap(value);
      if (isEqual(siteIds, selectedSiteMap)) return state;
      newState = {
        ...state,
        [key]: {
          ...previousFilterState,
          selectedSiteMap: mergeMaps(siteIds, selectedSiteMap)
        }
      };
    }

    return newState ? recalculatePatientDatabaseFilters(newState) : state;
  }

  if (key === THERAPEUTIC_AREA_FILTER || key === INDICATION_FILTER) {
    if (isEqual(value, previousFilterState.value)) return state;

    return recalculatePatientDatabaseFilters({
      ...state,
      [key]: {
        ...previousFilterState,
        value
      }
    });
  }

  return state;
}

function recalculatePatientDatabaseFilters(state) {
  let nextState = state;

  for (let index = 0; index < RECALCULATION_LIMIT; index++) {
    const previousSelection = getActiveSelectionSnapshot(nextState);
    nextState = recalculateSelections(recalculateOptions(nextState));
    const nextSelection = getActiveSelectionSnapshot(nextState);

    if (isEqual(previousSelection, nextSelection)) {
      return nextState;
    }
  }

  return nextState;
}

function recalculateOptions(state) {
  let nextState = recalculateStudies(state);
  nextState = recalculateSites(nextState);
  nextState = recalculateTherapeuticAreas(nextState);
  nextState = recalculateIndications(nextState);
  return nextState;
}

function recalculateSelections(state) {
  let nextState = recalculateSelectedStudyIds(state);
  nextState = recalculateSelectedStudies(nextState);
  nextState = recalculateSelectedSiteIds(nextState);
  nextState = recalculateSelectedSites(nextState);
  nextState = recalculateSelectedValues(nextState, THERAPEUTIC_AREA_FILTER);
  nextState = recalculateSelectedValues(nextState, INDICATION_FILTER);
  return nextState;
}

function getFilteredOrderedUniqueStudies(studySites, state) {
  const filteredStudySites = getFilteredStudySites(studySites, state, STUDY_FILTER);
  const studies = filteredStudySites.reduce(function(acc, { study }) {
    if (study) {
      acc.push(study);
    }
    return acc;
  }, []);

  const uniqueStudies = orderBy(
    uniqBy(studies, 'id').filter(study => study.id !== GENERAL_INTEREST_ID),
    'name',
    'asc'
  );

  return orderOptionsWithGeneralInterest(
    uniqueStudies,
    state.ssu?.selectedStudyMap,
    { projectCode: '' },
    filteredStudySites.some(isGeneralInterestStudySite)
  );
}

function getFilteredOrderedUniqueSites(studySites, state) {
  const filteredStudySites = getFilteredStudySites(studySites, state, SITE_FILTER);
  const sites = filteredStudySites.map(({ site }) => site);

  const uniqueSites = orderBy(
    uniqBy(sites, 'id').filter(site => site && site.id !== GENERAL_INTEREST_ID),
    'name',
    'asc'
  );

  return orderOptionsWithGeneralInterest(
    uniqueSites,
    state.ssu?.selectedSiteMap,
    {},
    filteredStudySites.some(isGeneralInterestSite)
  );
}

function orderOptionsWithGeneralInterest(options, selectedMap = {}, generalInterestProps = {}, includeGeneralInterest) {
  const source = includeGeneralInterest
    ? [
        {
          id: GENERAL_INTEREST_ID,
          name: GENERAL_INTEREST_NAME,
          ...generalInterestProps
        },
        ...options
      ]
    : options;

  return orderBy(
    source.map(option => ({
      ...option,
      checked: selectedMap[option.id] ? SELECTED_OPTION_GROUP : UNSELECTED_OPTION_GROUP
    })),
    ['checked', option => (option.id === GENERAL_INTEREST_ID ? 0 : 1), option => (option.name || '').toLowerCase()],
    ['asc', 'asc', 'asc']
  );
}

function recalculateStudies(state) {
  const studySites = state.ssu?.studySites || [];
  return {
    ...state,
    ssu: {
      ...state.ssu,
      studies: getFilteredOrderedUniqueStudies(studySites, state)
    }
  };
}

function recalculateSites(state) {
  const studySites = state.ssu?.studySites || [];
  return {
    ...state,
    ssu: {
      ...state.ssu,
      sites: getFilteredOrderedUniqueSites(studySites, state)
    }
  };
}

function recalculateTherapeuticAreas(state) {
  const studySites = state.ssu?.studySites || [];
  return {
    ...state,
    therapeuticArea: {
      ...state.therapeuticArea,
      source: getFilteredDistinctValues(studySites, state, THERAPEUTIC_AREA_FILTER, ssu => ssu.study?.therapeuticArea)
    }
  };
}

function recalculateIndications(state) {
  const studySites = state.ssu?.studySites || [];
  return {
    ...state,
    indication: {
      ...state.indication,
      source: getFilteredDistinctValues(studySites, state, INDICATION_FILTER, ssu => ssu.study?.indication)
    }
  };
}

function recalculateSelectedStudies(state) {
  const { studies, selectedStudyMap } = state.ssu;
  const selectedStudies = studies.filter(({ id }) => selectedStudyMap[id]);
  return {
    ...state,
    ssu: {
      ...state.ssu,
      selectedStudies
    }
  };
}

function recalculateSelectedStudyIds(state) {
  const { studies, selectedStudyMap } = state.ssu;
  return {
    ...state,
    ssu: {
      ...state.ssu,
      selectedStudyMap: studies.reduce(function(studyIdsMap, study) {
        studyIdsMap[study.id] = has(selectedStudyMap, study.id) ? selectedStudyMap[study.id] : false;
        return studyIdsMap;
      }, {})
    }
  };
}

function recalculateSelectedSites(state) {
  const { sites, selectedSiteMap } = state.ssu;
  const selectedSites = sites.filter(({ id }) => selectedSiteMap[id]);
  return {
    ...state,
    ssu: {
      ...state.ssu,
      selectedSites
    }
  };
}

function recalculateSelectedSiteIds(state) {
  const { sites, selectedSiteMap } = state.ssu;
  return {
    ...state,
    ssu: {
      ...state.ssu,
      selectedSiteMap: sites.reduce(function(siteIdsMap, site) {
        siteIdsMap[site.id] = has(selectedSiteMap, site.id) ? selectedSiteMap[site.id] : false;
        return siteIdsMap;
      }, {})
    }
  };
}

function recalculateSelectedValues(state, key) {
  const selectedIds = collectIdsSet(state[key]?.value);
  return {
    ...state,
    [key]: {
      ...state[key],
      value: state[key].source.filter(({ id }) => selectedIds.has(id))
    }
  };
}

function getFilteredDistinctValues(studySites, state, excludedFilter, extractor) {
  const seen = new Set();
  const filteredStudySites = getFilteredStudySites(studySites, state, excludedFilter);

  if (isOnlyGeneralInterestStudySelected(state)) {
    return [];
  }

  const values = filteredStudySites
    .map(extractor)
    .filter(val => !isBlank(val) && !seen.has(val) && seen.add(val))
    .sort()
    .map(val => ({ id: val, name: val }));

  return [
    ...(filteredStudySites.some(studySite => isEmptyFilterValue(studySite, extractor))
      ? [{ id: EMPTY_VALUE_ID, name: EMPTY_VALUE_NAME }]
      : []),
    ...(filteredStudySites.some(isGeneralInterestStudySite)
      ? [{ id: GENERAL_INTEREST_ID, name: GENERAL_INTEREST_NAME }]
      : []),
    ...values
  ];
}

function getFilteredStudySites(studySites, state, excludedFilter) {
  const selectionSets = {
    selectedStudyIds: collectMapIdsSet(state.ssu?.selectedStudyMap),
    selectedSiteIds: collectMapIdsSet(state.ssu?.selectedSiteMap),
    selectedTherapeuticAreas: collectIdsSet(state.therapeuticArea?.value),
    selectedIndications: collectIdsSet(state.indication?.value)
  };

  return studySites.filter(
    studySite =>
      matchesStudySelection(studySite, state, excludedFilter, selectionSets) &&
      matchesSiteSelection(studySite, state, excludedFilter, selectionSets) &&
      matchesTherapeuticAreaSelection(studySite, excludedFilter, selectionSets) &&
      matchesIndicationSelection(studySite, excludedFilter, selectionSets)
  );
}

function matchesStudySelection({ study }, state, excludedFilter, { selectedStudyIds }) {
  if (excludedFilter === STUDY_FILTER) {
    return true;
  }

  const generalInterestSelected = isSelected(state.ssu?.selectedStudyMap, GENERAL_INTEREST_ID);
  return (
    (selectedStudyIds.size === 0 && !generalInterestSelected) ||
    (study && selectedStudyIds.has(study.id)) ||
    (!study && generalInterestSelected)
  );
}

function matchesSiteSelection({ site }, state, excludedFilter, { selectedSiteIds }) {
  if (excludedFilter === SITE_FILTER) {
    return true;
  }

  const generalInterestSelected = isSelected(state.ssu?.selectedSiteMap, GENERAL_INTEREST_ID);
  return (
    (selectedSiteIds.size === 0 && !generalInterestSelected) ||
    (site && selectedSiteIds.has(site.id)) ||
    (!site && generalInterestSelected)
  );
}

function matchesTherapeuticAreaSelection({ study }, excludedFilter, { selectedTherapeuticAreas }) {
  if (excludedFilter === THERAPEUTIC_AREA_FILTER) {
    return true;
  }

  return (
    selectedTherapeuticAreas.size === 0 ||
    (!study && selectedTherapeuticAreas.has(GENERAL_INTEREST_ID)) ||
    (study && isBlank(study.therapeuticArea) && selectedTherapeuticAreas.has(EMPTY_VALUE_ID)) ||
    (study?.therapeuticArea && selectedTherapeuticAreas.has(study.therapeuticArea))
  );
}

function matchesIndicationSelection({ study }, excludedFilter, { selectedIndications }) {
  if (excludedFilter === INDICATION_FILTER) {
    return true;
  }

  return (
    selectedIndications.size === 0 ||
    (!study && selectedIndications.has(GENERAL_INTEREST_ID)) ||
    (study && isBlank(study.indication) && selectedIndications.has(EMPTY_VALUE_ID)) ||
    (study?.indication && selectedIndications.has(study.indication))
  );
}

function isOnlyGeneralInterestStudySelected(state) {
  return (
    isSelected(state.ssu?.selectedStudyMap, GENERAL_INTEREST_ID) &&
    collectMapIdsSet(state.ssu?.selectedStudyMap).size === 0
  );
}

function isGeneralInterestStudySite({ study }) {
  return !study;
}

function isGeneralInterestSite({ site }) {
  return !site;
}

function isEmptyFilterValue(studySite, extractor) {
  return !isGeneralInterestStudySite(studySite) && isBlank(extractor(studySite));
}

function isBlank(value) {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function isSelected(selectedMap = {}, id) {
  return Boolean(selectedMap[id]);
}

function collectIdsMap(item) {
  return item.reduce(function(accumulator, { id }) {
    accumulator[id] = true;
    return accumulator;
  }, {});
}

function collectIdsSet(items = []) {
  return new Set(items.map(({ id }) => id).filter(Boolean));
}

function collectMapIdsSet(selectedMap = {}) {
  return new Set(Object.keys(selectedMap).filter(id => id !== GENERAL_INTEREST_ID && selectedMap[id]));
}

function collectSelectedMapIds(selectedMap = {}) {
  return Object.keys(selectedMap)
    .filter(id => selectedMap[id])
    .sort();
}

function collectSelectedValueIds(items = []) {
  return items.map(({ id }) => id).sort();
}

function mergeMaps(a, b) {
  return assignInWith(a, b, a => a || false);
}

function getActiveSelectionSnapshot(state) {
  return {
    studies: collectSelectedMapIds(state.ssu?.selectedStudyMap),
    sites: collectSelectedMapIds(state.ssu?.selectedSiteMap),
    therapeuticAreas: collectSelectedValueIds(state.therapeuticArea?.value),
    indications: collectSelectedValueIds(state.indication?.value)
  };
}

function setFilterStatuses(schema, status) {
  return {
    ...schema,
    ssu: {
      ...schema?.ssu,
      status
    },
    therapeuticArea: {
      ...schema?.therapeuticArea,
      status
    },
    indication: {
      ...schema?.indication,
      status
    }
  };
}
